import { Box, Stack, Tooltip, Typography } from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import { useMemberInfo } from "../hooks/use-member-info";
import { DespleaIcon } from "./icons/desplega-icon";

const SPECIAL_USERS_MAP: Record<string, string> = {
  system: "desplega.ai",
  api: "API",
  agent: "desplega.ai",
  qa_automation_engineer: "desplega.ai",
  auto_agent: "desplega.ai",
  pw_reporter: "Playwright",
};

export const CreatedByColumn = <T extends { created_by: string }>(
  params: GridRenderCellParams<T>,
) => {
  const { getMember } = useMemberInfo();
  const rawCreatedBy = params.row.created_by;

  // Argus-driven runs arrive with two attribution shapes:
  //   - `argus[<instance_id>]` — gateway-wide fallback, tagged at the
  //     instance level (pre-0.10.3 sandboxes and any bash subprocess
  //     that bypasses the per-session tool).
  //   - `agent:<pi_session_id>` — per-session attribution stamped by
  //     the gateway's per-session bash `spawnHook` (argus 0.10.3+).
  // Both render as "<logo> Argus"; the tooltip disambiguates.
  const argusInstanceMatch = rawCreatedBy?.match(/^argus\[([^\]]+)\]$/);
  const argusAgentMatch = rawCreatedBy?.match(/^agent:(.+)$/);
  if (argusInstanceMatch || argusAgentMatch) {
    const tooltip = argusInstanceMatch
      ? `Argus instance ${argusInstanceMatch[1]}`
      : `Argus session ${argusAgentMatch![1]}`;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          height: "100%",
          width: "100%",
        }}
      >
        <Tooltip title={tooltip}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DespleaIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography variant="body2" noWrap>
              Argus
            </Typography>
          </Stack>
        </Tooltip>
      </Box>
    );
  }

  const member = getMember(rawCreatedBy);
  let displayName = member.name;

  // Apply special user mapping
  if (SPECIAL_USERS_MAP[displayName]) {
    displayName = SPECIAL_USERS_MAP[displayName];
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        height: "100%",
        width: "100%",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {member.imageUrl && (
          <Box
            component="img"
            src={member.imageUrl}
            alt={member.name}
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        )}
        <Tooltip title={member.tooltip}>
          <Typography variant="body2" noWrap>
            {displayName}
          </Typography>
        </Tooltip>
      </Stack>
    </Box>
  );
};
